import type { FirebaseOptions } from '@firebase/app';
import { initializeApp } from 'firebase/app';
import { getToken, initializeAppCheck } from 'firebase/app-check';
import { getAuth, initializeRecaptchaConfig } from 'firebase/auth';

import { getErrorMessage } from '../utils/errors';

import { createBackendAppCheckProvider } from './appCheckProvider';

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

const { VITE_FIREBASE_CONFIG, VITE_RECAPTCHA_SITE_KEY, VITE_API_URL } = import.meta.env;

let config: FirebaseOptions = {
  apiKey: 'AIzaSyCiD65koXhtncDwDUf--EkteCLWOoJToCc',
  authDomain: window.location.host,
  projectId: 'crowncoinscasino-4a9d7',
  storageBucket: 'crowncoinscasino-4a9d7.appspot.com',
  messagingSenderId: '921668979741',
  appId: '1:921668979741:web:7510ff61cb70cd1a450af4',
  measurementId: 'G-LRT39ZG844',
};

try {
  const parsed = JSON.parse(VITE_FIREBASE_CONFIG) as FirebaseOptions;
  if (parsed) {
    config = parsed;
  }
} catch (e) {
  const errorMessage = getErrorMessage(e);
  console.error(`Failed to parse VITE_FIREBASE_CONFIG: ${errorMessage}`);
}

export const firebase = initializeApp(config);

// player-backend assesses the reCAPTCHA Enterprise token and mints the App Check token. While its
// `app_check_backend_enabled` flag is off it answers `disabled` and the provider falls back to Google's
// exchange (what ReCaptchaEnterpriseProvider does); the key must stay registered in App Check for that.
const appCheckProvider = createBackendAppCheckProvider({
  apiUrl: VITE_API_URL,
  siteKey: VITE_RECAPTCHA_SITE_KEY,
  firebase: config,
});

const appCheck = initializeAppCheck(firebase, {
  provider: appCheckProvider.provider,

  // Optional argument. If true, the SDK automatically refreshes App Check
  // tokens as needed.
  isTokenAutoRefreshEnabled: true,
});

// Firebase error strings embed the failing request, which can carry the web API
// key as a `key=` param. Attestation material never belongs in a log.
const scrubSecrets = (message: string) => message.replace(/key=[^&\s)]+/gi, 'key=[redacted]');

// App Check failures are otherwise completely invisible. The SDK swallows the
// error, treats a 403 as unrecoverable and throttles for 24h, then attaches a
// dummy token to every request — so Identity Platform answers 401 "App Check
// token is invalid" and the user sees a generic "Something went wrong". Nothing
// reaches NewRelic, so a chronic invalid-attestation rate stays undetectable.
//
// Not routed through devError(): that is gated on the `enable_client_logs` flag
// via LaunchDarkly, which has not initialized this early in the boot sequence.
void getToken(appCheck).catch((e: unknown) => {
  const code = e && typeof e === 'object' && 'code' in e ? String(e.code) : 'unknown';
  const message = scrubSecrets(getErrorMessage(e));

  console.error(`AppCheck token fetch failed [${code}]: ${message}`);

  // Deliberately no reCAPTCHA token and no site key in the payload.
  window.newrelic?.noticeError(new Error('AppCheckTokenFailed'), {
    appCheckErrorCode: code,
    appCheckErrorMessage: message,
    // `appCheck/throttled` means an earlier 403 already locked this session out
    // for 24h, so it marks a repeat symptom rather than a fresh failure.
    isThrottled: code === 'appCheck/throttled',
    // Which exchange the failing call went to: 'backend' (player-backend) or 'google' (flag off).
    appCheckProvider: appCheckProvider.lastPath() ?? 'unknown',
    context: 'initializeAppCheck',
  });
});

// connectAuthEmulator(firebaseAuth, 'http://localhost:9099');

export const firebaseAuth = getAuth(firebase);

// Pre-warm the IDP recaptchaConfig so the user's first sign-in / sign-up call
// doesn't go out without a token (MISSING_RECAPTCHA_TOKEN + an SDK auto-retry).
// enterprise.js itself is loaded by html/index.html.
let recaptchaReadyPromise: Promise<void> | null = null;
export function prewarmRecaptcha(): Promise<void> {
  if (!recaptchaReadyPromise) {
    recaptchaReadyPromise = initializeRecaptchaConfig(firebaseAuth).catch((e) => {
      console.warn('reCAPTCHA Enterprise pre-warm failed:', getErrorMessage(e));
    });
  }
  return recaptchaReadyPromise;
}

export const getFirebaseAuthToken = async () => {
  const token = await firebaseAuth.currentUser?.getIdToken();
  return token;
};

if (new URLSearchParams(window.location.search).has('__test')) {
  void firebaseAuth.setPersistence({ type: 'SESSION' });
}
